import { NestedTreeControl } from '@angular/cdk/tree';
import { ChangeDetectorRef, Component, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, from, of, throwError, zip } from 'rxjs';
import { takeUntil, take, switchMap } from 'rxjs/operators';
import { FileStat } from 'webdav';
import { CommonSnackBarService } from '../../../../_services/common-snack-bar.service';
import { FileSystemAccessService } from '../../../../_services/file-system-access.service';
import { NetworkStatusService } from '../../../../_services/network-status.service';
import { SpinnerService } from '../../../../_services/spinner.service';
import { PLATFORM, IMAGE_FILETYPE, UPLOAD_RESULT_CODE, TenantConfig } from '../../../../app.constants';
import { ImportIplexGImageWrapper, FileWrapper, FolderWrapper } from '../../../../inspection/dialogs/data-upload-dialog/save-iplex-g-images/save-iplex-g-images.component';
import { IplexGConnectModeService } from '../../../services/iplex-g-connect-mode.service';
import { IplexGSyncService } from '../../../services/iplex-g-sync.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SelectLocalFolderPathDialogComponent } from '../select-local-folder-path-dialog/select-local-folder-path-dialog.component';
import { FILE_SYSTEM, IPC_CHANNEL } from '../../../electron.constants';
import { UploadProgressDialogService } from '../../../../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.service';
import { UploadProgressDialogComponent } from '../../../../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.component';
import { UtilsService } from '../../../../_services/_utils.service';
import { CommonConfirmDialogComponent } from '../../../../common-dialog/common-confirm-dialog.component';
import { ConflictImage, UploadOption } from '../../../../_objects/inspection-image';
import { ImageConflictDialogService } from '../../../../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.service';
import { ImageConflictDialogComponent } from '../../../../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.component';
import { MarkItem } from '../../../../_objects/mark';
import { TenantConfigService } from '../../../../_services/http-api/tenant-config.service';
import { CommonDeleteDialogComponent } from '../../../../common-dialog/common-delete-dialog.component';
import { MarksStateService } from "../../../../_services/mark-state/marks-state.service";
import { ElectronService } from '../../../../electron.service';

export class FileStatWrapper extends FolderWrapper {
  files: FileWrapper[] = [];
  folderChildren: FileStatWrapper[];

  get isSelectedAllFiles() {
    if (!this.isParent) {
      return this.files?.length > 0
        ? this.files.every(file => file.isSelected)
        : this.isSelected;
    }

    if (this.folderChildren?.length > 0) {
      return this.files?.length > 0
        ? this.files.every(file => file.isSelected) && this.folderChildren.every(folder => folder.isSelectedAllFiles)
        : this.folderChildren.every(folder => folder.isSelectedAllFiles);
    } else {
      return this.files?.length > 0
        ? this.files.every(file => file.isSelected)
        : this.isSelected;
    }
  }

  get isUnselectedAllFiles() {
    if (!this.isParent) {
      return this.files?.length > 0
        ? this.files.every(file => !file.isSelected)
        : !this.isSelected;
    }

    if (this.folderChildren?.length > 0) {
      return this.files?.length > 0
        ? this.files.every(file => !file.isSelected) && this.folderChildren.every(folder => folder.isUnselectedAllFiles)
        : this.folderChildren.every(folder => folder.isUnselectedAllFiles);
    } else {
      return this.files?.length > 0
        ? this.files.every(file => !file.isSelected)
        : !this.isSelected;
    }
  }
  get isIndeterminateAllFiles() {

    if (!this.isParent) {
      return this.files?.length > 0
        ? !this.isSelectedAllFiles && !this.isUnselectedAllFiles
        : false;
    }

    let match = false, unMatch = false;
    if (this.folderChildren?.length > 0) {
      const isSelectedAllFolder = this.folderChildren.every(folder => folder.isSelected);
      const isUnSelectedAllFolder = this.folderChildren.every(folder => !folder.isSelected);

      if (!isSelectedAllFolder && !isUnSelectedAllFolder) {
        return true;
      }

      const isSelectedAllFiles = this.folderChildren.every(folder => folder.isSelectedAllFiles);
      const isUnSelectedAllFiles = this.folderChildren.every(folder => folder.isUnselectedAllFiles);
      if (!isSelectedAllFiles && !isUnSelectedAllFiles) {
        return true;
      }

      if (isSelectedAllFolder || isSelectedAllFiles) {
        match = true;
      }

      if (isUnSelectedAllFolder && isUnSelectedAllFiles) {
        unMatch = true;
      }

    }

    if (this.files?.length > 0) {
      return this.files.some(file => {
        if (file.isSelected) {
          match = true;
        } else {
          unMatch = true;
        }
        return match && unMatch;
      });
    }

    return false;
  }

  constructor(file: FileStat, parentFolder: FileStat, children: FileStatWrapper[] = [], isSelected = false) {
    super(file, parentFolder, children, isSelected);
    this.files = [];
  }

  switchSelectAllChild() {
    const isAllSelect = this.isSelectedAllFiles;
    if (this.isParent) {
      if (!this.hasChildren && this.files?.length < 1) {
        const current =  this.isSelected;
        this.isSelected = !current;
      }

      if (this.files?.length > 0) {
        this.files.forEach(file => file.isSelected = !isAllSelect);
      }
      if (this.hasChildren) {
        this.folderChildren.forEach(folder => {
          if (folder.files?.length > 0) {
            folder.files.forEach(file => file.isSelected = !isAllSelect);
          }
          folder.isSelected = !isAllSelect;
        });
      }
      this.isSelected = !isAllSelect;
    } else {
      if (this.files?.length > 0) {
        const isAllSelect =  this.files.every(file => file.isSelected);
        this.files.forEach(file => file.isSelected = !isAllSelect);
        this.isSelected = !!isAllSelect;
      } else {
        const current =  this.isSelected;
        this.isSelected = !current;
      }
    }
  }

  get hasFiles() {
    return this.files?.length > 0;
  }

  get hasContents() {
    return this.hasFiles || this.hasChildren;
  }
}

@Component({
    selector: 'app-iplex-g-contents-select-dialog',
    templateUrl: './iplex-g-contents-select-dialog.component.html',
    styleUrls: ['./iplex-g-contents-select-dialog.component.scss'],
    standalone: false
})
export class IplexGContentsSelectDialogComponent implements OnInit, OnDestroy {

  @Output() public importIplexGImages = new EventEmitter<ImportIplexGImageWrapper>();
  viewFiles: FileWrapper[] = [];
  isDeletedWithUploading = false;
  activeFolder: FileStatWrapper;

  completeFileCount = 0;
  failureFileCount = 0;
  successFileCount = 0;
  progressDialogRef: MatDialogRef<UploadProgressDialogComponent, any>;

  nestedTreeControl: NestedTreeControl<FileStatWrapper>;
  nestedDataSource: MatTreeNestedDataSource<FileStatWrapper>;

  fileSizeStreamEvent: any = null;

  targetFolders: FileStatWrapper[] = [];
  targetFiles: FileWrapper[] = [];
  conflictList: ConflictImage[] = [];
  marks: MarkItem[];

  conflictDialogRef: MatDialogRef<ImageConflictDialogComponent, boolean>;
  private onDestroy$ = new Subject();
  private onCancel$ = new Subject();
  readonly displayedColumns: string[] = ['checkbox', 'filename', 'date', 'size', 'menu'];
  readonly dateFormat = 'yyyy/MM/dd H:mm';
  readonly receiveVideoMimetype = 'application/octet-stream';
  readonly mp4Mimetype = 'video/mp4';
  readonly jsonMimetype = 'application/json';

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isDeviceConnectMode() {
    return this.isOffline && this.iplexGConnectModeService.isHttpsConnectMode;
  }

  get selectedImageCount() {
    if (!this.nestedDataSource?.data) {
      return 0;
    }

    return this.getSelectedFileList(this.nestedDataSource.data)
      .filter(_ => _.isSelected && this.isImageFile(_.fileStat.mime, _.fileStat.size, _.fileStat.basename))
      .length;
  }

  get selectedVideoCount() {
    if (!this.nestedDataSource?.data) {
      return 0;
    }
    return this.getSelectedFileList(this.nestedDataSource.data)
      .filter(_ => _.isSelected && this.isVideoFile(_.fileStat.size, _.fileStat.basename))
      .length;
  }

  get selectedTotalFileSize() {
    let result = 0;

    if (!this.nestedDataSource?.data) {
      return result;
    }

    const fileSizeList = this.getSelectedFileList(this.nestedDataSource.data)
      .filter(_ => _.isSelected)
      .map(_ => _.fileStat.size);

    fileSizeList.forEach(_ => result += _);
    return result;
  }

  get isSelectedFilesAll(): boolean {
    return this.viewFiles.every(imageFile => imageFile.isSelected);
  }

  get isUnSelectedFilesAll(): boolean {
    return this.viewFiles.every(imageFile => !imageFile.isSelected);
  }

  get isIndeterminateFilesAll(): boolean {
    let match = false, unMatch = false;
    if (this.viewFiles && this.viewFiles.length > 0) {
      this.viewFiles.forEach(file => {
        if (file.isSelected) {
          match = true;
        } else {
          unMatch = true;
        }
      });
    }
    return match && unMatch;
  }

  get isSelectedFoldersAll(): boolean {
    return this.nestedDataSource
      && this.nestedDataSource.data.every(folder => folder.isSelectedAllFiles);
  }

  get isUnSelectedFoldersAll(): boolean {
    return this.nestedDataSource
      && this.nestedDataSource.data.every(folder => folder.isUnselectedAllFiles);
  }

  get isIndeterminateFoldersAll(): boolean {
    return !this.isSelectedFoldersAll && !this.isUnSelectedFoldersAll;
  }

  get defaultMark() {
    const tenantConfig = this.tenantConfigService.tenantConfig;
    return tenantConfig.find(_ => _.key === TenantConfig.DefaultMarkWhenUploadingNoMarkImage.KEY).value as number;
  }

  constructor(
    private networkStatusService: NetworkStatusService,
    private iplexGConnectModeService: IplexGConnectModeService,
    private iplexGSyncService: IplexGSyncService,
    private fileSysAccessService: FileSystemAccessService,
    private snackBar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private cd: ChangeDetectorRef,
    private translate: TranslateService,
    private dialog: MatDialog,
    private electronService: ElectronService,
    private zone: NgZone,
    private utils: UtilsService,
    private uploadProgressDialogService: UploadProgressDialogService,
    private dialogRef: MatDialogRef<IplexGContentsSelectDialogComponent>,
    private imageConflictDialogService: ImageConflictDialogService,
    private markService: MarksStateService,
    private tenantConfigService: TenantConfigService
  ) { }

  ngOnInit(): void {
    this.viewFiles = [];
    this.nestedDataSource = new MatTreeNestedDataSource();
    this.nestedTreeControl = new NestedTreeControl<FileStatWrapper>(this.getChildren);

    this.oscSpinnerService.show(true);
    this.iplexGConnectModeService.isWevDAVModeObserver
      .subscribe(isWebDaVMode => {
        if (!isWebDaVMode) {
          this.cancelFileUpload();
        }
      });
    this.iplexGConnectModeService.isHttpsModeObserver()
      .subscribe(isHttpsMode => {
        if (!isHttpsMode) {
          this.progressDialogRef.close();
          this.dialogRef.close();
        }
      });

    this.iplexGSyncService.onWebDAVConnectMode()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        isSuccess => {
          if (!isSuccess) {
            this.snackBar.open(this.translate.instant('iplexGConnectModeService.failedConnect'));
            this.oscSpinnerService.hide(true);
            return;
          }

          this.markService.markItems$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(mark => this.marks = mark);

          this.getAllContents();
        },
        error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('iplexGConnectModeService.failedConnect'));
          this.oscSpinnerService.hide(true);
        }
      );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();

    if (this.fileSizeStreamEvent) {
      this.electronService.removeAllListeners(IPC_CHANNEL.WEB_DAV.STREAM_SIZE);
    }

    this.oscSpinnerService.show(true);
    this.iplexGSyncService.cancelFileTransfer();
    this.iplexGSyncService.offWebDAVConnectMode()
      .pipe(take(1))
      .subscribe(
        _ => this.oscSpinnerService.hide(true),
        error => {
          console.log(error);
          this.oscSpinnerService.hide(true);
        });
  }

  public hasNestedChild(_: number, folder: FileStatWrapper) {
    return folder?.isParent;
  }

  private getChildren = (node: FileStatWrapper) => node.folderChildren;

  getAllContents() {
    let fileStatList: FileStatWrapper[] = [];
    this.oscSpinnerService.show(true);
    this.iplexGSyncService.getDirectoriesInSDcard('/')
      .pipe(takeUntil(this.onDestroy$))
      .pipe(switchMap(folders => {
        fileStatList = folders.map(folder => new FileStatWrapper(folder, null));
        const obs = fileStatList.map(folder => this.iplexGSyncService.getALLContentsFromInSDcard(folder.fileStat.filename));
        return zip(...obs);
      }))
      .subscribe(contentsList => {
        contentsList.forEach((contents, i) => {
          const directories = contents.filter(file => file.type === 'directory');
          fileStatList[i].folderChildren = directories.map(fileStat => new FileStatWrapper(fileStat, fileStatList[i].fileStat));

          const files = contents
            .filter(file => file.type === 'file')
            .filter(file => this.isReceivedFile(file.mime, file.size, file.basename));
          const bookMarkFiles = contents
            .filter(file => file.type === 'file')
            .filter(file => file.basename && file.basename.toLowerCase().split('.').pop().match('json|jsn'));
          const audioFiles = contents
            .filter(file => file.type === 'file')
            .filter(file => file.basename && file.basename.toLowerCase().split('.').pop().match('wav'));

          if (files && files.length > 0) {
            files.forEach(file =>{
              const withOutMime = this.getWithoutFileName(file.basename);
              const bookMarkFile = bookMarkFiles
                .find(bm => this.getWithoutFileName(bm.basename) === withOutMime);
              const audioFile = audioFiles
                .find(audio => this.getWithoutFileName(audio.basename) === withOutMime);

              const parentFolderName = file.filename.replace(`/${file.basename}`, '');
              const isExistParentFolder = fileStatList[i].folderChildren.some(folder => {
                if (folder.fileStat.filename === parentFolderName) {
                  folder.files.push(new FileWrapper(file, false, fileStatList[i].fileStat, folder.fileStat, bookMarkFile, audioFile));
                  return true;
                }
                return false;
              });

              if (!isExistParentFolder) {
                fileStatList[i].files.push(new FileWrapper(file, false, fileStatList[i].fileStat, fileStatList[i].fileStat, bookMarkFile, audioFile));
              }
            });
          }
        });

        this.updateDataSource(fileStatList, null);
        this.oscSpinnerService.hide(false);
      },
      error => {
        console.log(error);
        this.oscSpinnerService.hide(false);
      });
  }

  activateFolder(folder: FileStatWrapper) {
    if (!folder) { return; }

    this.activeFolder = folder;
    if (this.activeFolder.isParent) {
      let files = this.activeFolder.files;
      this.activeFolder.folderChildren.forEach(folder => files = files.concat(folder.files));
      this.viewFiles = files;
    } else {
      this.viewFiles = this.activeFolder.files;
    }
  }

  folderSelect(folder: FileStatWrapper) {
    if (!folder) { return; }

    if (folder.isParent || folder.files.length > 0) {
      folder.switchSelectAllChild();
    } else {
      const currentSelected = folder.isSelected;
      folder.isSelected = !currentSelected;
    }
  }

  selectFilesAll() {
    if (!this.isOffline) {
      return;
    }

    const isAllSelected = this.viewFiles.every(file => file.isSelected);
    this.viewFiles
      .forEach(file => file.isSelected = !isAllSelected);

    if (!isAllSelected) {
      this.updateParentFolderSelection(this.activeFolder);
    }

  }

  selectFoldersAll() {
    if (this.isOffline) {
      const isAllSelected = this.isSelectedFoldersAll;
      this.nestedDataSource.data.forEach(parentFolder => {
        parentFolder.isSelected = !isAllSelected;

        if (parentFolder.files?.length > 0) {
          parentFolder.files.forEach(file => file.isSelected = !isAllSelected);
        }

        if (parentFolder?.folderChildren.length > 0) {
          parentFolder.folderChildren.forEach(folder => {
            folder.isSelected = !isAllSelected;
            folder.files.forEach(file => file.isSelected = !isAllSelected);
          });
        }
      });
    }
  }

  selectDestination() {
    const dialogRef = this.openFolderSelectDialog();
    dialogRef.componentInstance.dialogSubmit
      .subscribe(destFolderPath => {
        this.dataTransfer(destFolderPath);
        dialogRef.close();
      });
  }

  dataTransfer(destPath: string) {
    this.completeFileCount = 0;
    this.failureFileCount = 0;
    this.successFileCount = 0;

    this.targetFolders = this.getSelectedFolderList(this.nestedDataSource.data);
    this.targetFiles = this.getSelectedFileList(this.nestedDataSource.data);

    this.fileSizeStreamEvent = this.electronService
      .onEvent(IPC_CHANNEL.WEB_DAV.STREAM_SIZE, (_event, reply) => {
        this.zone.run(() => this.uploadProgressDialogService.countLoadedFileSize(reply));
      });

    if (this.targetFolders.length > 0 && this.targetFiles.length < 1) {
      this.oscSpinnerService.show(true);
      if (this.targetFolders.length > 0) {
        this.createFolderProcess(destPath);
      }
      return;
    }

    const getDeviceFilePromises = this.targetFiles
      .map(wrapper => {
        const contentPath = wrapper.fileStat.filename;

        const mimeType = this.receiveVideoMimetype === wrapper.fileStat.mime
          ? this.mp4Mimetype
          : wrapper.fileStat.mime;

        const $saved = new Subject<boolean>();
        const eventChannel = IPC_CHANNEL.WEB_DAV.SAVE_STREAM + contentPath;
        this.electronService
          .onEvent(eventChannel, (_event, _reply) => $saved.next(true));

        let tempDestPath = null;
        const savePath = destPath + contentPath;
        let renamedFileName: string = null;
        let isExistMainFile = false;
        return this.fileSysAccessService.getSaveBlobFolderPath()
          .then(blobPath => {
            tempDestPath = blobPath + FILE_SYSTEM.PATH.TEMP;
            return this.iplexGSyncService.getFileFromSDcardStream(contentPath, tempDestPath + contentPath)
              .toPromise();
          })
          .then(_ => zip(
            $saved.asObservable(),
            this.fileSysAccessService.isFileExists(savePath, false)
          ).toPromise()
          )
          .then(async values => {
            $saved.unsubscribe();
            this.electronService.removeAllListeners(eventChannel);

            const isSaved = values[0];
            isExistMainFile = values[1];
            const tempTransferFilePath = tempDestPath + contentPath;

            if (isExistMainFile) {
              this.conflictList.push(new ConflictImage(null, wrapper.fileStat.basename, savePath, null, tempTransferFilePath, savePath, false, null));
              this.conflictDialogRef = this.imageConflictDialogService.open(this.conflictList, { applyToAllEnabled: false, cancelEnabled: false });

              this.conflictDialogRef.componentInstance.dialogAction
                .subscribe(async conflictAction => {
                  this.imageConflictDialogService.nextConflictImage();
                  switch (conflictAction.uploadOption) {
                    case UploadOption.Replace:
                      // Replace
                      this.renameTempFile(tempDestPath + contentPath, savePath, destPath,wrapper, isSaved, renamedFileName);
                      break;
                    case UploadOption.Skip:
                      // Delete Temp file
                      this.fileSysAccessService.deleteFile(FILE_SYSTEM.PATH.TEMP + contentPath)
                        .then(_ =>  this.successUpload(contentPath, destPath));
                      break;
                    case UploadOption.KeepBoth:
                    default:
                      // Rename Temp file
                      const tempName: string = this.genFileName(contentPath, this.marks, false);
                      let withoutMime: string = this.utils.getFilePathWithoutExtension(tempName);
                      const tempMark = this.utils.getMarkFromFilenameSuffix(withoutMime, this.marks);
                      const mime: string = this.utils.getExtension(tempName);
                      withoutMime = this.utils.getFileNameWithoutMark(withoutMime, this.marks);

                      let fileSequenceNumber: number = 0;
                      let searchTargetFileName = '';
                      let isExist:boolean = true;
                      do {
                        fileSequenceNumber++;
                        renamedFileName = `${destPath}${withoutMime} (${fileSequenceNumber})${tempMark}`;
                        searchTargetFileName = `${renamedFileName}.${mime}`;
                        isExist = await this.fileSysAccessService.isFileExists(searchTargetFileName, false)
                          .toPromise();
                      } while (isExist);

                      this.renameTempFile(tempTransferFilePath, searchTargetFileName, destPath, wrapper, isSaved, renamedFileName);
                  }
                });

            } else {
              this.renameTempFile(tempTransferFilePath, savePath, destPath, wrapper, isSaved, renamedFileName);
            }
          })
          .catch(error => {
            console.log(error);
            $saved.unsubscribe();
            this.electronService.removeAllListeners(eventChannel);
            return error;
          });
      });

    const startTime = performance.now();
    this.progressDialogRef = this.openReceiveProgressDialog(this.targetFiles.length, this.selectedTotalFileSize, startTime);
    this.progressDialogRef.afterClosed()
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(
        _ => this.cancelFileUpload(),
        error => console.log(error));

    this.progressDialogRef.componentInstance.dialogClose
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(_ => {
        this.oscSpinnerService.hide(true);
        this.progressDialogRef.close(true);
      });

    this.utils.chainToPromises(getDeviceFilePromises, 12)
      .then(() => {
        this.electronService.removeAllListeners(IPC_CHANNEL.WEB_DAV.STREAM_SIZE);
      })
      .catch(error => {
        this.oscSpinnerService.hide(false);
        this.snackBar.open(this.translate.instant('dataUploadDialog.errorReceiveFromIPLEX'));
        this.electronService.removeAllListeners(IPC_CHANNEL.WEB_DAV.STREAM_SIZE);
        console.log(error);
        throw new Error('failed file transfer from IPLEX');
      });
  }

  public openReceiveProgressDialog(totalFileCount: number, totalFileSize:number, startTime: number) {
    this.uploadProgressDialogService.initReceiveMode(totalFileCount, totalFileSize, startTime, 'directFileTransfer');
    return this.dialog.open(UploadProgressDialogComponent, {
      width: UploadProgressDialogComponent.defaultDialogWidth,
      maxHeight: UploadProgressDialogComponent.defaultDialogMaxHeight,
      minHeight: UploadProgressDialogComponent.defaultDialogMinHeight,
      disableClose: true,
      data: {
        isTransfer: true
      }
    });
  }

  private successUpload(fullPath: string, destPath: string): void {
    // 進捗を設定
    this.uploadProgressDialogService.completeFileCount += 1;
    this.uploadProgressDialogService.successFileCount += 1;
    this.uploadProgressDialogService.imageFileResultList[fullPath] = UPLOAD_RESULT_CODE.SUCCESS;

    // アップロード終了処理
    if (this.uploadProgressDialogService.completeFileCount === this.uploadProgressDialogService.totalFileCount) {
      if (this.uploadProgressDialogService.failureFileCount === 0) {
        this.uploadProgressDialogService.isDisabledCancel = true;

        if (this.targetFolders.length > 0) {
          if (this.isDeletedWithUploading) {
            this.oscSpinnerService.displayMessage('Completing the move process.');
          } else {
            this.oscSpinnerService.show(true);
          }
          this.createFolderProcess(destPath);
        } else {
          this.close();
          this.openSuccessfulDialog();
        }
      } else {
        this.uploadProgressDialogService.isError = true;
        this.iplexGSyncService.cancelFileTransfer();
      }
    }
  }

  private failureUpload(fullPath: string): void {
    // 進捗を設定
    this.uploadProgressDialogService.completeFileCount += 1;
    this.uploadProgressDialogService.failureFileCount += 1;
    this.uploadProgressDialogService.imageFileResultList[fullPath] = UPLOAD_RESULT_CODE.FAILED;
    this.uploadProgressDialogService.failedFileNameList.push(fullPath);

    // アップロード終了処理
    if (this.uploadProgressDialogService.completeFileCount === this.uploadProgressDialogService.totalFileCount) {
      if ( this.uploadProgressDialogService.failureFileCount !== 0) {
        this.snackBar.open('An error occurred transfer files.');
        this.uploadProgressDialogService.isDisabledCancel = true;
        if (this.progressDialogRef) {
          this.progressDialogRef.close();
        }

        if (this.dialogRef) {
          this.dialogRef.close();
        }
      } else {
        this.iplexGSyncService.cancelFileTransfer();
      }
    }
  }

  private cancelFileUpload(): boolean {
    this.uploadProgressDialogService.completeFileCount = 0;
    this.onCancel$.next(undefined);
    this.iplexGSyncService.cancelFileTransfer();
    return true;
  }

  getSelectedFolderList(targetFolders: FileStatWrapper[]): FileStatWrapper[] {
    let filteredFolders: FileStatWrapper[] = [];
    targetFolders.forEach(folder => {
      if (folder.hasChildren) {
        const childrenFolders = this.getSelectedFolderList(folder.folderChildren);
        filteredFolders = filteredFolders.concat(childrenFolders);
      }

      if (folder.isSelected || folder.isSelectedAllFiles || folder.isIndeterminateAllFiles) {
        filteredFolders.push(folder);
      }
    });

    return filteredFolders;
  }

  getSelectedFileList(targetFolders: FileStatWrapper[]): FileWrapper[] {
    let filteredFiles: FileWrapper[] = [];
    targetFolders.forEach(folder => {
      if (folder.files.length > 0) {
        folder.files.forEach(file => {
          if (file.isSelected) {
            filteredFiles.push(file);
          }
        });
      }

      if (folder.hasChildren) {
        const childrenFiles = this.getSelectedFileList(folder.folderChildren);
        filteredFiles = filteredFiles.concat(childrenFiles);
      }
    });

    return filteredFiles;
  }

  updateDataSource(folders: FileStatWrapper[], expandNode: FileStatWrapper) {
    this.nestedDataSource.data =  [];
    this.nestedDataSource.data  = folders;

    const target = folders.find(folder => folder?.fileStat.basename === expandNode?.fileStat.basename);
    if (target) {
      this.nestedTreeControl.expand(target);
    }

    this.cd.detectChanges();
  }

  updateParentFolderSelection(folder: FileStatWrapper) {
    if (folder.isParent) {
      return;
    }

    this.nestedDataSource.data.some(parentFolder => {
      if (parentFolder.fileStat.basename === folder.parentFolder.basename) {
        if (!parentFolder.isUnSelectedAllChild) {
          parentFolder.isSelected = true;
        }
        return true;
      }
      return false;
    });
  }

  openFolderSelectDialog() {
    return this.dialog.open(SelectLocalFolderPathDialogComponent,{
      width: '640px',
      panelClass: 'dialog-scroll'
    });
  }

  private isReceivedFile(filetype: string, fileSize: number, filename: string): boolean {
    return this.isImageFile(filetype, fileSize, filename)
      || this.isVideoFile(fileSize, filename);
  }

  private isImageFile(filetype: string, fileSize: number, filename: string) {
    return (filetype.match(IMAGE_FILETYPE.JPEG)
    || filetype.match(IMAGE_FILETYPE.PNG)
    || filetype.match(IMAGE_FILETYPE.BMP)
    || filetype.match(IMAGE_FILETYPE.TIF)
    || filetype.match(IMAGE_FILETYPE.TIFF)
    || filename?.toLowerCase().split('.').pop().match('heic|heif'))
    && fileSize > 0;
  }

  private isVideoFile(fileSize: number, filename: string) {
    if (fileSize <= 0) return false;
    const extension = filename.toLowerCase().split('.').pop();
    const validExtensions = ["mp4", "avi"];
    return extension ? validExtensions.includes(extension) : false;
  }

  getWithoutFileName(fileName: string) {
    const fileNameArray = fileName.toLowerCase().split('.');
    fileNameArray.pop();
    return fileNameArray.join('.');
  }

  createFolderProcess(destPath: string) {
    const parentFolderObs = this.targetFolders
      .filter(folder => folder.isParent)
      .map(folder => this.genCreateFolderOb(folder, destPath, this.isDeletedWithUploading));
    const childFolderObs = this.targetFolders
      .filter(folder => !folder.isParent)
      .map(folder => this.genCreateFolderOb(folder, destPath, this.isDeletedWithUploading));

    if (parentFolderObs.length > 0) {
      const createFolderObs = childFolderObs.length < 1
        ? zip(...parentFolderObs)
        : zip(...childFolderObs).pipe(take(1))
          .pipe(switchMap(_ => {
            return parentFolderObs.length > 0
              ? zip(...parentFolderObs)
              : of(_);
          }));

      createFolderObs.pipe(take(1))
        .subscribe(_ => {
          this.zone.run(() => {
            this.oscSpinnerService.hide(true);
            this.close();
            this.openSuccessfulDialog();
          });
        });
    } else if (childFolderObs.length > 0) {
      zip(...childFolderObs)
        .pipe(take(1))
        .subscribe(_ => {
          this.zone.run(() => {
            this.oscSpinnerService.hide(true);
            this.close();
            this.openSuccessfulDialog();
          });
        });
    }
  }

  genCreateFolderOb(folder: FolderWrapper, destPath: string, toDelete = false) {
    const folderPath = (destPath + folder.fileStat.filename).replace(/\\/g, '\/');
    return from(this.fileSysAccessService.createFolder(folderPath, false))
      .pipe(switchMap(isCreated => {
        if (!toDelete) {
          return of(isCreated);
        }

        return this.iplexGSyncService.getContentsFromInSDcard(folder.fileStat.filename)
          .pipe(switchMap(contents => {
            const directories = contents.filter(cont => cont.type === 'directory');
            const files = contents.filter(cont => cont.type === 'file');
            if (directories.length < 1 && files.length < 1) {
              return this.iplexGSyncService.deleteContentInSDcard(folder.fileStat.filename);
            }
            return of(isCreated);
          }));
      }));
  }

  close() {
    if (this.progressDialogRef) {
      this.progressDialogRef.close();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openSuccessfulDialog() {
    return this.dialog.open(CommonConfirmDialogComponent, {
      disableClose: true,
      data: {
        firstMessage: this.translate.instant('iplexGContentsSelectDialogComponent.successTransferSuccessful_1'),
        secondMessage: this.translate.instant('iplexGContentsSelectDialogComponent.successTransferSuccessful_2'),
        dialogType: 'confirmation',
        trueBtnText: this.translate.instant('commonConfirmDialog.ok'),
        displayCancelOption: false
      }
    });
  }

  private genFileName(fileName: string, marks: MarkItem[], isDataPlate: boolean) {
    let withoutMime: string = this.utils.getFilePathWithoutExtension(fileName);
    const mime: string = this.utils.getExtension(fileName);
    const marksWithPositiveId: Array<MarkItem> = marks.filter(x => x.identifier > 0);
    if (!marksWithPositiveId.find(_ => withoutMime.endsWith('_' + _.code))) {
      // default mark の設定
      const markItem = marksWithPositiveId
        .find(x => x.identifier === this.defaultMark);

      if (markItem) {
        const fileNameSuffix = '_' + markItem.code;
        withoutMime = withoutMime + fileNameSuffix;
      }
    }
    return withoutMime + '.' + mime;
  }

  private renameTempFile(oldFilePath: string, newFilePath: string, destPath: string, wrapper: FileWrapper, isSaved: boolean, renamedFileName: string) {
    const contentPath = wrapper.fileStat.filename;
    const mimeType = this.receiveVideoMimetype === wrapper.fileStat.mime
      ? this.mp4Mimetype
      : wrapper.fileStat.mime;
    this.fileSysAccessService.renameFilePath(oldFilePath, newFilePath)
      .then(isRenameSuccess => {
        return  isRenameSuccess && this.isDeletedWithUploading
          ? this.iplexGSyncService.deleteContentInSDcard(contentPath).toPromise()
          : of(isSaved && isRenameSuccess).toPromise();
      })
      .then(isMainFileSaved => {
        let saveBmFileFileOb: Observable<boolean> = of(null);
        if (mimeType === this.mp4Mimetype && wrapper?.bookMarkStat) {
          const mime: string = this.utils.getExtension(wrapper.bookMarkStat.filename);
          const saveBmFileName = renamedFileName !== null
            ? `${renamedFileName}.${mime}`
            : destPath + wrapper.bookMarkStat.filename;

          saveBmFileFileOb = this.iplexGSyncService.getFileFromSDcardStream(wrapper.bookMarkStat.filename, saveBmFileName, false)
            .pipe(switchMap(isSaved => {
              return isMainFileSaved && this.isDeletedWithUploading
                ? this.iplexGSyncService.deleteContentInSDcard(wrapper.bookMarkStat.filename).pipe(take(1))
                : of(isSaved);
            }));
        }

        let saveAudioFileFileOb: Observable<boolean> = of(null);
        if (wrapper?.audioStat) {
          const mime: string = this.utils.getExtension(wrapper.audioStat.filename);
          const saveAudioFileName = renamedFileName !== null
            ? `${renamedFileName}.${mime}`
            : destPath + wrapper.audioStat.filename;

          saveAudioFileFileOb = this.iplexGSyncService
            .getFileFromSDcardStream(wrapper.audioStat.filename, saveAudioFileName, false)
            .pipe(switchMap(isSaved => {
              return isMainFileSaved && this.isDeletedWithUploading
                ? this.iplexGSyncService.deleteContentInSDcard(wrapper.audioStat.filename).pipe(take(1))
                : of(isSaved);
            }));
        }

        return zip(of(isMainFileSaved), saveBmFileFileOb, saveAudioFileFileOb).toPromise();
      })
      .then(values => {
        const isSaved = values[0];
        if (isSaved) {
          this.successUpload(contentPath, destPath);
        } else {
          this.failureUpload(contentPath);
        }
        return of(isSaved).toPromise();
      })
      .then(_ => _);
  }

  folderDelete(folder: FileStatWrapper) {
    const isContain = folder.files.length > 0 || folder.hasChildren;
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent,{
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: {
        target: folder.fileStat.filename,
        deleteNames: [folder.fileStat.basename],
        message: isContain
          ? this.translate.instant('saveIplexGImagesComponent.folderContainsDeleteMessage')
          : this.translate.instant('saveIplexGImagesComponent.folderDeleteMessage')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.oscSpinnerService.show(true);
      this.iplexGSyncService.deleteContentInSDcard(folder.fileStat.filename)
        .pipe(take(1))
        .subscribe(
          isSuccess => {
            if (!isSuccess) {
              throwError('Delete Failed');
            }

            this.oscSpinnerService.hide(true);
            let dataSource = this.nestedDataSource.data;

            dataSource.some(x => {
              if (x.fileStat.filename === folder.fileStat.filename) {
                dataSource = dataSource
                  .filter(_ => _.fileStat.filename !== folder.fileStat.filename);
                return true;
              }

              if (x.hasChildren) {
                return x.folderChildren.some(y => {
                  if (y.fileStat.filename === folder.fileStat.filename) {
                    x.folderChildren = x.folderChildren.filter(_ => _.fileStat.filename !== folder.fileStat.filename);
                    return true;
                  }
                  return false;
                });
              }
              return false;
            });

            this.updateDataSource(dataSource, null);

            if (this.activeFolder?.fileStat.filename === folder?.fileStat.filename) {
              this.activeFolder = null;
              this.viewFiles = [];
            } else {
              dataSource.some(x => {
                if (x.fileStat.filename === this.activeFolder?.fileStat.filename) {
                  this.activateFolder(x);
                  return true;
                }

                if (x.hasChildren) {
                  return x.folderChildren.some(y => {
                    if (y.fileStat.filename === this.activeFolder?.fileStat.filename) {
                      this.activateFolder(y);
                      return true;
                    }
                    return false;
                  });
                }
                return false;
              });
            }

            this.snackBar.open(this.translate.instant('saveIplexGImagesComponent.deleteSuccessfully'));
            this.cd.detectChanges();
          },
          error => {
            console.log(error);
            this.snackBar.open(this.translate.instant('saveIplexGImagesComponent.deleteFailed'));
            this.oscSpinnerService.hide(true);
          });
    });
  }

  fileDelete(wrapper: FileWrapper) {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent,{
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: {
        target: wrapper.fileStat.filename,
        deleteNames: [wrapper.fileStat.basename],
        message: this.translate.instant('saveIplexGImagesComponent.fileDeleteMessage')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.oscSpinnerService.show(true);
      this.iplexGSyncService.deleteContentInSDcard(wrapper.fileStat.filename)
        .pipe(take(1))
        .pipe(switchMap(isSuccess =>  {
          if (!isSuccess) {
            throwError('Delete Failed');
          }

          const mimeType = this.receiveVideoMimetype === wrapper.fileStat.mime
            ? this.mp4Mimetype
            : wrapper.fileStat.mime;

          const deleteBmFileFileO = mimeType === this.mp4Mimetype && wrapper?.bookMarkStat
            ? this.iplexGSyncService.deleteContentInSDcard(wrapper.bookMarkStat.filename).pipe(take(1))
            : of(true);

          const deleteAudioFileFileO = wrapper?.audioStat
            ? this.iplexGSyncService.deleteContentInSDcard(wrapper.audioStat.filename).pipe(take(1))
            : of(true);

          return zip(deleteBmFileFileO, deleteAudioFileFileO);
        }))
        .subscribe(
          _ => {
            this.oscSpinnerService.hide(true);
            this.updateData(wrapper);
            this.snackBar.open(this.translate.instant('saveIplexGImagesComponent.deleteSuccessfully'));
          },
          error => {
            console.log(error);
            this.snackBar.open(this.translate.instant('saveIplexGImagesComponent.deleteFailed'));
            this.oscSpinnerService.hide(true);
          });
    });
  }

  updateData(target: FileWrapper) {
    this.viewFiles = this.viewFiles.filter(file => file.fileStat.basename !== target.fileStat.basename);
    this.activeFolder.files = this.activeFolder.files.filter(file => file.fileStat.basename !== target.fileStat.basename);
    if (this.activeFolder.hasChildren) {
      this.activeFolder.folderChildren.forEach(folder => {
        folder.files = folder.files.filter(file => file.fileStat.basename !== target.fileStat.basename);
      });
    }

    this.nestedDataSource.data.some(folder => {
      const updated = folder.files.some(file => {
        if (file.fileStat.basename === target.fileStat.basename) {
          folder.files = folder.files.filter(_ => file.fileStat.basename !== target.fileStat.basename);
          return true;
        }
        return false;
      });

      if (!updated) {
        if (folder.hasChildren) {
          return folder.folderChildren.some(file => {
            if (file.fileStat.basename === target.fileStat.basename) {
              folder.files = folder.files.filter(_ => file.fileStat.basename !== target.fileStat.basename);
              return true;
            }
            return false;
          });
        }
      }

      return false;
    });
  }

}
